import React, { useMemo } from "react";
import { Loader2 } from "lucide-react";

import {
  DraftContractPreview,
  EditorSidebar,
  EditorSectionsPanel,
  AiExtractPanel,
  DeliverablesPanel,
  KpisPanel,
  AiAssistantPanel,
} from "../components/contract-editor";
import { ContextualReviewPanel } from "../components/contract-editor/ContextualReviewPanel";
import { PlaceholderReviewPanel } from "../components/contract-editor/PlaceholderReviewPanel";
import { useContractEditor } from "../components/contract-editor/useContractEditor";
import { EditorToolbar } from "../components/contract-editor/EditorToolbar";
import { TemplateSheet } from "../components/contract-editor/TemplateSheet";
import { ClauseSheet } from "../components/contract-editor/ClauseSheet";

export default function ContractEditorPage() {
  const editor = useContractEditor();

  const addedClauseIds = useMemo(
    () => new Set(editor.formData.additional_clauses.map((c) => c.clause_id)),
    [editor.formData.additional_clauses]
  );

  if (editor.loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contract-editor-page">
      <EditorToolbar editor={editor} />

      {/* Sheets (template & clause side panels) */}
      <TemplateSheet
        open={editor.templateSheetOpen}
        onOpenChange={editor.setTemplateSheetOpen}
        templates={editor.templates}
        selectedTemplate={editor.selectedTemplate}
        onApplyTemplate={editor.handleApplyTemplate}
      />
      <ClauseSheet
        open={editor.clauseSheetOpen}
        onOpenChange={editor.setClauseSheetOpen}
        clauses={editor.clauses}
        addedClauseIds={addedClauseIds}
        onAddClause={editor.handleAddClause}
      />

      {/* Main Content + AI Review Panel */}
      <div className="flex gap-0">
        <div className={`${editor.contextualReviewOpen ? "flex-1 min-w-0" : "w-full"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Metadata Panel — Left */}
            {!editor.editorExpanded && (
              <EditorSidebar formData={editor.formData} isEditing={editor.isEditing} onChange={editor.handleChange} />
            )}

            {/* Editor Panel — Right */}
            <div className={`${editor.editorExpanded ? "lg:col-span-3" : "lg:col-span-2"} space-y-6`}>
              <PlaceholderReviewPanel
                formData={editor.formData}
                setFormData={editor.setFormData}
                onApply={() => editor.setPlaceholdersApplied(true)}
              />

              {editor.contentVisible && (
                <>
                  <EditorSectionsPanel
                    formData={editor.formData}
                    setFormData={editor.setFormData}
                    editorExpanded={editor.editorExpanded}
                    setEditorExpanded={editor.setEditorExpanded}
                    onUpdateSection={editor.handleUpdateSection}
                    setActiveSection={editor.setActiveSection}
                  />

                  <AiExtractPanel
                    aiExtractText={editor.aiExtractText}
                    setAiExtractText={editor.setAiExtractText}
                    aiExtracting={editor.aiExtracting}
                    aiResults={editor.aiResults}
                    setAiResults={editor.setAiResults}
                    onExtract={editor.handleAiExtract}
                    formData={editor.formData}
                    setFormData={editor.setFormData}
                  />

                  <DeliverablesPanel formData={editor.formData} setFormData={editor.setFormData} />
                  <KpisPanel formData={editor.formData} setFormData={editor.setFormData} />

                  <AiAssistantPanel
                    aiPrompt={editor.aiPrompt}
                    setAiPrompt={editor.setAiPrompt}
                    aiGenerating={editor.aiGenerating}
                    onGenerate={editor.handleAiDraft}
                    activeSection={editor.activeSection}
                    sections={editor.formData.sections}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Contextual AI Review Panel */}
        {editor.contextualReviewOpen && (
          <div className="w-[380px] shrink-0 border-l h-[calc(100vh-180px)] sticky top-24" data-testid="contextual-review-sidebar">
            <ContextualReviewPanel
              formData={editor.formData}
              reviewMode="drafting"
              onApplySuggestion={editor.handleApplySuggestion}
              onClose={() => editor.setContextualReviewOpen(false)}
            />
          </div>
        )}
      </div>

      {/* Draft Contract Preview */}
      <DraftContractPreview
        formData={editor.formData}
        selectedTemplate={editor.selectedTemplate}
        open={editor.draftPreviewOpen}
        onOpenChange={editor.setDraftPreviewOpen}
      />
    </div>
  );
}
